import type { KpiComparisonPeriod } from "@types";

export type MaintenanceType = "PREVENTIVE" | "CORRECTIVE" | "SINGLE" | "SUBSCRIPTION";

export type MaintenanceStatus = "DONE" | "SCHEDULED" | "CANCELLED";

export type MaintenanceAlertSeverity = "CRITICAL" | "WARNING" | "INFO";

export type MaintenanceAlertType = "CORRECTIVE_CONCENTRATION" | "COST_PER_KM_INCREASE" | "COST_PER_VEHICLE_DECREASE";

export const MAINTENANCE_ALERT_TYPES: MaintenanceAlertType[] = [
  "CORRECTIVE_CONCENTRATION",
  "COST_PER_KM_INCREASE",
  "COST_PER_VEHICLE_DECREASE",
];

export interface MaintenanceSummaryMetric {
  value: number;
  previousValue: number;
  variationPercent: number | null;
  currentPeriod?: KpiComparisonPeriod | null;
  previousPeriod?: KpiComparisonPeriod | null;
}

export interface MaintenanceSummary {
  totalSpent: MaintenanceSummaryMetric;
  averageCostPerKm: MaintenanceSummaryMetric & { kmDriven: number };
  averageCostPerVehicle: MaintenanceSummaryMetric;
}

export interface MaintenanceTypeTotal {
  type: MaintenanceType;
  amount: number;
}

export interface MaintenanceSubcategoryTotal {
  subcategory: string;
  amount: number;
}

export interface MaintenanceVehicleCost {
  vehicle: string;
  amount: number;
  costPerKm: number;
}

export interface MaintenanceTransaction {
  id: string;
  dateTime: string;
  vehicle: string;
  type: MaintenanceType;
  subcategory: string;
  paymentMethod: string;
  amount: number;
  status: MaintenanceStatus;
}

export interface MaintenanceAlert {
  id: string;
  type: MaintenanceAlertType;
  severity: MaintenanceAlertSeverity;
  title: string;
  description: string;
  occurredAt: string;
}

export interface MaintenanceAlertTotals {
  total: number;
  alerts: Partial<Record<MaintenanceAlertType, number>>;
}

export interface MaintenanceAlertMetric {
  label: string;
  value: number;
  highlighted: boolean;
}

export interface MaintenanceAlertDetail extends MaintenanceAlert {
  metrics: MaintenanceAlertMetric[];
}

export const MAINTENANCE_SUBCATEGORIES_MOCK: MaintenanceSubcategoryTotal[] = [
  { subcategory: "Funilaria", amount: 3973.77 },
  { subcategory: "Reparos elétricos", amount: 2930 },
  { subcategory: "Suspensão", amount: 2830 },
  { subcategory: "Revisão de freios", amount: 1280.95 },
  { subcategory: "Pneus", amount: 1200 },
  { subcategory: "Troca de óleo", amount: 980.5 },
];

export const MAINTENANCE_VEHICLES_MOCK: MaintenanceVehicleCost[] = [
  { vehicle: "Titã 540", amount: 25500, costPerKm: 0.4 },
  { vehicle: "Colosso 6x4", amount: 10800, costPerKm: 0.4 },
  { vehicle: "Mamute 500 Max", amount: 8900, costPerKm: 0.4 },
  { vehicle: "Brutus 580 HD", amount: 8900, costPerKm: 0.4 },
  { vehicle: "Atlas 620 v8", amount: 8900, costPerKm: 0.4 },
  { vehicle: "Delivery 11.180", amount: 6200, costPerKm: 0.35 },
];

type TransactionRow = [
  dateTime: string,
  vehicle: string,
  type: MaintenanceType,
  subcategory: string,
  amount: number,
  status: MaintenanceStatus,
  paymentMethod?: string,
];

const TRANSACTION_ROWS: TransactionRow[] = [
  ["2026-08-28T14:32:00", "Constellation 25.480", "PREVENTIVE", "Funilaria", 4280, "DONE"],
  ["2026-08-27T10:15:00", "Meteor 29.520", "SINGLE", "Revisão de freios", 7850, "DONE", "Crédito"],
  ["2026-08-25T08:47:00", "Delivery 11.180", "PREVENTIVE", "Revisão de freios", 2640, "DONE", "Débito"],
  ["2026-08-22T16:20:00", "Constellation 31.320", "SINGLE", "Revisão de freios", 5920, "DONE"],
  ["2026-08-20T09:05:00", "Meteor 28.480", "SUBSCRIPTION", "Pneus", 0, "DONE"],
  ["2026-08-18T13:42:00", "Delivery 9.180", "SINGLE", "Pneus", 1980, "DONE"],
  ["2026-08-15T11:18:00", "Constellation 24.280", "PREVENTIVE", "Pneus", 6340, "DONE"],
  ["2026-08-13T15:30:00", "Meteor 29.520", "CORRECTIVE", "Suspensão", 3120, "DONE"],
  ["2026-08-11T09:50:00", "Constellation 25.480", "CORRECTIVE", "Reparos elétricos", 2430, "DONE"],
  ["2026-08-08T14:05:00", "Delivery 11.180", "PREVENTIVE", "Troca de óleo", 890, "DONE"],
  ["2026-08-06T10:40:00", "Constellation 31.320", "CORRECTIVE", "Funilaria", 5210, "DONE"],
  ["2026-08-04T08:15:00", "Meteor 28.480", "PREVENTIVE", "Revisão de freios", 1760, "DONE"],
  ["2026-08-01T16:55:00", "Delivery 9.180", "SINGLE", "Suspensão", 2380, "DONE"],
  ["2026-07-29T11:25:00", "Constellation 24.280", "CORRECTIVE", "Reparos elétricos", 3960, "DONE"],
  ["2026-07-26T13:10:00", "Titã 540", "PREVENTIVE", "Pneus", 7420, "DONE"],
  ["2026-07-23T09:35:00", "Colosso 6x4", "CORRECTIVE", "Funilaria", 4810, "DONE"],
  ["2026-07-20T15:45:00", "Mamute 500 Max", "SINGLE", "Troca de óleo", 640, "DONE"],
  ["2026-07-17T10:20:00", "Brutus 580 HD", "PREVENTIVE", "Revisão de freios", 2150, "DONE"],
  ["2026-07-14T14:50:00", "Atlas 620 v8", "CORRECTIVE", "Suspensão", 3380, "DONE"],
  ["2026-07-11T08:05:00", "Titã 540", "SUBSCRIPTION", "Pneus", 0, "DONE"],
  ["2026-07-08T12:30:00", "Colosso 6x4", "PREVENTIVE", "Funilaria", 1890, "DONE"],
  ["2026-07-05T16:15:00", "Mamute 500 Max", "SINGLE", "Reparos elétricos", 2760, "DONE"],
  ["2026-07-02T09:45:00", "Brutus 580 HD", "CORRECTIVE", "Pneus", 4520, "DONE"],
  ["2026-06-28T11:00:00", "Atlas 620 v8", "PREVENTIVE", "Troca de óleo", 910, "DONE"],
  ["2026-06-25T13:35:00", "Constellation 25.480", "SINGLE", "Suspensão", 3040, "DONE"],
];

export const MAINTENANCE_TRANSACTIONS_MOCK: MaintenanceTransaction[] = TRANSACTION_ROWS.map(
  ([dateTime, vehicle, type, subcategory, amount, status, paymentMethod = "Boleto"], index) => ({
    id: String(index + 1),
    dateTime,
    vehicle,
    type,
    subcategory,
    paymentMethod,
    amount,
    status,
  })
);

const ALERT_TEMPLATES: Record<
  MaintenanceAlertType,
  { severity: MaintenanceAlertSeverity; title: string; description: string }
> = {
  CORRECTIVE_CONCENTRATION: {
    severity: "CRITICAL",
    title: "Alta concentração em manutenção corretiva",
    description: "Manutenções corretivas representam 56% dos gastos do período.",
  },
  COST_PER_KM_INCREASE: {
    severity: "WARNING",
    title: "Aumento do custo km rodado",
    description: "O custo por km aumentou 18,9% em relação ao mês anterior.",
  },
  COST_PER_VEHICLE_DECREASE: {
    severity: "INFO",
    title: "Redução do gasto médio por veículos",
    description: "O custo médio por veículo caiu 11% neste mês.",
  },
};

const ALERT_METRICS: Record<MaintenanceAlertType, MaintenanceAlertMetric[]> = {
  CORRECTIVE_CONCENTRATION: [
    { label: "Outras revisões", value: 124800.2, highlighted: false },
    { label: "Corretiva", value: 157527.78, highlighted: true },
  ],
  COST_PER_KM_INCREASE: [
    { label: "Custo/km anterior", value: 0.42, highlighted: false },
    { label: "Custo/km atual", value: 0.5, highlighted: true },
  ],
  COST_PER_VEHICLE_DECREASE: [
    { label: "Custo médio anterior", value: 0.64, highlighted: false },
    { label: "Custo médio atual", value: 0.57, highlighted: true },
  ],
};

type AlertRow = [type: MaintenanceAlertType, occurredAt: string];

const ALERT_ROWS: AlertRow[] = [
  ["CORRECTIVE_CONCENTRATION", "2026-08-28T14:32:00"],
  ["COST_PER_KM_INCREASE", "2026-08-27T10:15:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-25T08:47:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-22T16:20:00"],
  ["COST_PER_KM_INCREASE", "2026-08-20T09:05:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-18T13:42:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-15T11:18:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-13T15:30:00"],
  ["COST_PER_KM_INCREASE", "2026-08-11T09:50:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-08T14:05:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-08-06T10:40:00"],
  ["COST_PER_KM_INCREASE", "2026-08-04T08:15:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-08-01T16:55:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-29T11:25:00"],
  ["COST_PER_KM_INCREASE", "2026-07-26T13:10:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-23T09:35:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-20T15:45:00"],
  ["COST_PER_KM_INCREASE", "2026-07-17T10:20:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-14T14:50:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-11T08:05:00"],
  ["COST_PER_KM_INCREASE", "2026-07-08T12:30:00"],
  ["COST_PER_VEHICLE_DECREASE", "2026-07-05T16:15:00"],
  ["CORRECTIVE_CONCENTRATION", "2026-07-02T09:45:00"],
];

export const MAINTENANCE_ALERT_HISTORY_MOCK: MaintenanceAlert[] = ALERT_ROWS.map(([type, occurredAt], index) => ({
  id: String(index + 1),
  type,
  occurredAt,
  ...ALERT_TEMPLATES[type],
}));

const countAlertsByType = (type: MaintenanceAlertType) =>
  MAINTENANCE_ALERT_HISTORY_MOCK.filter((alert) => alert.type === type).length;

export const MAINTENANCE_ALERT_TOTALS_MOCK: MaintenanceAlertTotals = {
  total: MAINTENANCE_ALERT_HISTORY_MOCK.length,
  alerts: Object.fromEntries(MAINTENANCE_ALERT_TYPES.map((type) => [type, countAlertsByType(type)])),
};

export const buildAlertDetail = (alert: MaintenanceAlert): MaintenanceAlertDetail => ({
  ...alert,
  metrics: ALERT_METRICS[alert.type],
});
